<script setup lang="ts">
  import { getCurrentInstance, onMounted, ref, watch } from 'vue'
  import {
    useRouter,
    useRoute,
    appRouteDefinitions,
    routeDefinitions
  } from '@gat-ship/lib/router'
  import {
    appStateKey,
    StartupState,
    useStartupState
  } from '@gat-ship/lib/app-state'
  import { mapToAppError } from '@gat-ship/lib/error-handling'
  import {
    supportSessionContextKey,
    useSupportSession
  } from '@gat-ship/lib/support-session'
  import { userServiceKey, useUser } from '@gat-ship/lib/user'
  import { _useAppState } from '@gat-ship/lib/app-state'
  import { _useSignalR, signalRKey } from '@gat-ship/lib/signalr'
  import { _useFeatureFlag, featureFlagKey } from '@gat-ship/lib/feature-flag'
  import {
    _useGlobalSettings,
    globalSettingsKey
  } from '@gat-ship/lib/global-settings'
  import { useAuth0 } from '@gat-ship/lib/auth'
  import {
    type DatabaseAccessGrantedSignalRPayload,
    type DatabaseAccessRevokedSignalRPayload,
    signalREvent
  } from '@gat-ship/types/signal-r-events'
  import { icons } from '@gat-ship/icons'
  import appSplashScreen from './components/app-splash-screen.vue'

  const auth0 = useAuth0()
  const router = useRouter()
  const route = useRoute()
  const startupState = useStartupState()
  const supportSessionContext = useSupportSession()
  const userContext = useUser()
  const appStateContext = _useAppState()
  const signalRContext = _useSignalR()
  const featureFlagContext = _useFeatureFlag()
  const globalSettingsContext = _useGlobalSettings()

  async function isAuthenticated() {
    if (auth0.isAuthenticated.value) {
      return true
    }
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        if (
          startupState.startupState.value.includes(StartupState.Initializing)
        ) {
          // Problems with auth0.
          reject(new Error('User profile loading timed out after 60 seconds'))
        }
      }, 60000)
      const unwatch = watch(
        auth0.isAuthenticated,
        (newValue) => {
          if (newValue) {
            clearTimeout(timeout)
            unwatch()
            resolve(true)
          }
        },
        { immediate: true, deep: true }
      )
    })
  }

  const appReady = ref(false)

  /**
   * Reigsters/Provides all the services contexts to the app and adds them to the app's global properties.
   * @param app - The vue app instance.
   * @returns The contexts.
   */
  function provideContexts() {
    const app = getCurrentInstance()?.appContext.app
    if (app) {
      if (
        !Object.prototype.hasOwnProperty.call(
          app._context.provides,
          appStateKey
        )
      ) {
        app.provide(appStateKey, appStateContext)
      }
      if (
        !Object.prototype.hasOwnProperty.call(
          app._context.provides,
          supportSessionContextKey
        )
      ) {
        app.provide(supportSessionContextKey, supportSessionContext)
      }
      if (
        !Object.prototype.hasOwnProperty.call(
          app._context.provides,
          userServiceKey
        )
      ) {
        app.provide(userServiceKey, userContext)
      }
      if (
        !Object.prototype.hasOwnProperty.call(app._context.provides, signalRKey)
      ) {
        app.provide(signalRKey, signalRContext)
      }
      if (
        !Object.prototype.hasOwnProperty.call(
          app._context.provides,
          featureFlagKey
        )
      ) {
        app.provide(featureFlagKey, featureFlagContext)
      }
      if (
        !Object.prototype.hasOwnProperty.call(
          app._context.provides,
          globalSettingsKey
        )
      ) {
        app.provide(globalSettingsKey, globalSettingsContext)
      }
      app.config.globalProperties.$appState = appStateContext
      app.config.globalProperties.$user = userContext
      app.config.globalProperties.$icons = icons
      app.config.globalProperties.$routeDefinitions = routeDefinitions
      app.config.globalProperties.$featureFlag = featureFlagContext
      app.config.globalProperties.$settings = globalSettingsContext
    }
  }

  /**
   * Asynchronous function that initializes all application services.
   * Must be called to start the application initialization process.
   *
   * This function performs the following operations in sequence:
   * 1. Provides application contexts (support session, user, app state, SignalR)
   * 2. Waits for router to be ready
   * 3. Establishes SignalR connection
   * 4. Loads user access roles and user profile
   *
   * @async
   * @throws {Error} Throws an error if any part of the initialization process fails
   *
   * @example
   * ```typescript
   * try {
   *   await initializeApp();
   *   console.log('App successfully initialized');
   * } catch (error) {
   *   console.error('App initialization failed:', error);
   * }
   * ```
   */
  async function initializeApp() {
    try {
      provideContexts()
      // Create an abort controller for the current cache invalidation operation
      let currentCacheInvalidationController: AbortController | null = null

      await router.isReady()
      if (route.name === appRouteDefinitions.auth.signOut) {
        return
      }
      appStateContext.startupState.setStartupState(StartupState.Initializing)
      // Make sure the router is ready before we start the initialization process.

      const supportSessionId =
        await supportSessionContext.getSupportSessionIdFromRouteQuery()
      // Load user and tenant information
      const userIsAuthenticated = await isAuthenticated()
      if (!userIsAuthenticated) {
        throw new Error('User authentication failed')
      }
      const {
        tenantId,
        databaseId,
        tenantName,
        databaseName,
        userEmail,
        userId
      } = await userContext._loadUser()
      const database =
        databaseId && databaseName
          ? {
              key: databaseId,
              name: databaseName
            }
          : undefined
      await featureFlagContext._initialize({
        user: {
          key: userId,
          name: userEmail
        },
        tenant: {
          key: tenantId,
          name: tenantName
        },
        database
      })
      await globalSettingsContext._initialize({
        userId,
        databaseId
      })
      await signalRContext.connectToSignalR({
        tenantId,
        supportSessionToken:
          supportSessionContext.supportSessionToken.value ?? undefined
      })

      signalRContext.subscribe(
        signalREvent.Diagnostic.V1.DatabaseGroupJoined,
        () => {
          // console.log('Recieved')
        }
      )

      signalRContext.subscribe(
        signalREvent.Diagnostic.V1.TenantGroupJoined,
        () => {
          // console.log('Recieved')
        }
      )

      signalRContext.subscribe<DatabaseAccessGrantedSignalRPayload>(
        signalREvent.AccessControl.V1.DatabaseAccessGranted,
        async ({ message }) => {
          if (message.userId === userContext.id.value) {
            userContext._addDatabase({
              id: message.databaseId,
              name: message.databaseDisplayName,
              tenantId: message.tenantId,
              tenantName: userContext.userProfile.value?.homeTenantName ?? '',
              isDefault: false,
              affectedBySupportSession: false,
              isSelected: false
            })
            // await userContext.fetchDatabases()
          }
        }
      )

      signalRContext.subscribe<DatabaseAccessRevokedSignalRPayload>(
        signalREvent.AccessControl.V1.DatabaseAccessRevoked,
        async ({ message }) => {
          const currentDatabaseId = userContext.databases.value.current?.id
          if (message.userId === userContext.id.value) {
            // await userContext.cacheInvalidation.invalidateCache({
            //   force: true
            // })
            try {
              const result = await userContext.fetchDatabases()
              if (currentDatabaseId === message.databaseId) {
                if (result.databases.length === 0) {
                  window.location.reload()
                  return
                }
                userContext.switchDatabase(result.databases[0].id)
              }
            } catch (error) {
              window.location.reload()
            }
          }
        }
      )

      appStateContext.startupState.setStartupState(StartupState.ReadyForStartup)
    } catch (error: unknown) {
      const appError = mapToAppError(error)
      startupState.setStartupState(StartupState.Error)
      startupState.setStartupStateError(appError)
      // Wrap and re-throw with additional context for debugging
      throw error
    }
  }

  onMounted(async () => {
    await initializeApp()
    appReady.value = true
  })

  defineExpose({
    initializeApp
  })
</script>
<template>
  <slot v-if="appReady" />
  <app-splash-screen v-else />
</template>
